import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableSortLabel from '@mui/material/TableSortLabel';
import CircularProgress from '@mui/material/CircularProgress';
import AddIcon from '@mui/icons-material/Add';
import { useAdminUsers } from '../../hooks/useUsers';
import { formatDate, sortRows, toggleDir } from './adminUtils';

const LOCALE_LABELS = { hr: 'Hrvatski', si: 'Slovenski', ro: 'Rumunjski', ba: 'Bosanski', en: 'English' };

export default function AdminUsers() {
    const navigate = useNavigate();
    const { data: users = [], isLoading } = useAdminUsers();
    const [sort, setSort] = useState({ key: 'username', dir: 'asc' });

    const sorted = sortRows(users, sort.key, sort.dir, (r, k) => {
        if (k === 'username') return r.username;
        if (k === 'is_active') return r.is_active ? 1 : 0;
        if (k === 'alert_limit') return r.alert_limit;
        if (k === 'scheduled_searches_limit') return r.scheduled_searches_limit;
        if (k === 'briefing_limit') return r.briefing_limit;
        if (k === 'smart_alert_limit') return r.smart_alert_limit;
        if (k === 'locale') return r.locale;
        if (k === 'allowed_portal_count') return r.allowed_portal_count;
        if (k === 'timedate_created') return r.timedate_created;
        return '';
    });

    const sp = (key) => ({
        active: sort.key === key,
        direction: sort.key === key ? sort.dir : 'asc',
        onClick: () => setSort(prev => toggleDir(prev, key)),
    });

    if (isLoading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
    }

    return (
        <Box sx={{ maxWidth: 1100, mx: 'auto', px: 2, py: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50' }}>
                    Korisnici
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/admin/users/create')}
                    sx={{ textTransform: 'none', borderRadius: '20px' }}
                >
                    Novi korisnik
                </Button>
            </Box>
            <Paper sx={{ overflow: 'auto' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell sx={{ fontWeight: 600 }}><TableSortLabel {...sp('username')}>Korisnik</TableSortLabel></TableCell>
                            <TableCell sx={{ fontWeight: 600 }}><TableSortLabel {...sp('is_active')}>Status</TableSortLabel></TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="center"><TableSortLabel {...sp('alert_limit')}>Alerti</TableSortLabel></TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="center"><TableSortLabel {...sp('scheduled_searches_limit')}>Auto. pretrage</TableSortLabel></TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="center"><TableSortLabel {...sp('briefing_limit')}>Briefingovi</TableSortLabel></TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="center"><TableSortLabel {...sp('smart_alert_limit')}>Smart Alerti</TableSortLabel></TableCell>
                            <TableCell sx={{ fontWeight: 600 }}><TableSortLabel {...sp('locale')}>Jezik</TableSortLabel></TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="center"><TableSortLabel {...sp('allowed_portal_count')}>Portala</TableSortLabel></TableCell>
                            <TableCell sx={{ fontWeight: 600 }}><TableSortLabel {...sp('timedate_created')}>Kreiran</TableSortLabel></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sorted.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ color: '#999', py: 3 }}>
                                    Nema korisnika.
                                </TableCell>
                            </TableRow>
                        )}
                        {sorted.map((u) => (
                            <TableRow
                                key={u.id}
                                hover
                                onClick={() => navigate(`/admin/users/${u.id}`)}
                                sx={{ cursor: 'pointer', opacity: u.is_active ? 1 : 0.5 }}
                            >
                                <TableCell sx={{ fontSize: 13, fontWeight: 500 }}>{u.username}</TableCell>
                                <TableCell>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            px: 1, py: 0.25, borderRadius: 1, fontWeight: 600,
                                            backgroundColor: u.is_active ? '#e8f5e9' : '#f5f5f5',
                                            color: u.is_active ? '#2e7d32' : '#999',
                                        }}
                                    >
                                        {u.is_active ? 'Aktivan' : 'Neaktivan'}
                                    </Typography>
                                </TableCell>
                                <TableCell align="center" sx={{ fontSize: 13 }}>{u.alert_limit}</TableCell>
                                <TableCell align="center" sx={{ fontSize: 13 }}>{u.scheduled_searches_limit}</TableCell>
                                <TableCell align="center" sx={{ fontSize: 13 }}>{u.briefing_limit}</TableCell>
                                <TableCell align="center" sx={{ fontSize: 13 }}>{u.smart_alert_limit}</TableCell>
                                <TableCell sx={{ fontSize: 13 }}>{LOCALE_LABELS[u.locale] ?? u.locale}</TableCell>
                                <TableCell align="center" sx={{ fontSize: 13 }}>{u.allowed_portal_count}</TableCell>
                                <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{formatDate(u.timedate_created)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>
        </Box>
    );
}
